import React, { useState } from "react";
import {
  formatPlAmount,
  chartYAxisLabelsForAmount,
} from "../../utils/formatPlAmount";
import { formatIsoDateRangeOrdinalMonth } from "../../utils/formatIsoDateOrdinalMonth";

export interface WeekData {
  label: string;
  startDate?: string;
  endDate?: string;
  cashIn: number;
  cashOut: number;
}

export interface WeeklyCashMovementChartProps {
  weeks: WeekData[];
  monthLabel?: string;
  mtdNet?: string;
  loading?: boolean;
}

const CHART_HEIGHT_PX = 256;

export function WeeklyCashMovementChart({
  weeks,
  monthLabel = "February 2026",
  mtdNet,
  loading = false,
}: WeeklyCashMovementChartProps) {
  const [hoveredWeekIndex, setHoveredWeekIndex] = useState<number | null>(null);

  const maxVal = Math.max(
    ...weeks.flatMap((w) => [w.cashIn, w.cashOut]),
    1
  );
  const yAxisLabels = chartYAxisLabelsForAmount(maxVal, 5);

  if (loading) {
    return (
      <div className="flex grow shrink-0 basis-0 flex-col items-start gap-6 rounded-lg border border-solid border-neutral-border bg-default-background px-6 py-6">
        <div className="flex w-full items-center gap-6">
          <div className="h-4 w-24 animate-pulse rounded bg-neutral-200" />
          <div className="h-4 w-24 animate-pulse rounded bg-neutral-200" />
        </div>
        <div className="flex h-64 w-full items-end justify-around gap-2">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="flex-1 max-w-[48px] flex gap-1 items-end"
              style={{ height: CHART_HEIGHT_PX }}
            >
              <div
                className="flex-1 rounded-t-sm bg-success-200 animate-pulse"
                style={{ height: `${40 + Math.random() * 120}px` }}
              />
              <div
                className="flex-1 rounded-t-sm bg-error-200 animate-pulse"
                style={{ height: `${30 + Math.random() * 100}px` }}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex grow shrink-0 basis-0 flex-col items-start gap-6 rounded-lg border border-solid border-neutral-border bg-default-background px-6 py-6">
      <div className="flex w-full items-center gap-6">
        <div className="flex items-center gap-2">
          <div className="flex h-3 w-3 flex-none items-start rounded-sm bg-success-500" />
          <span className="text-caption font-caption text-subtext-color">
            Cash In
          </span>
          <span className="text-body-bold font-body-bold text-default-font">
            {formatPlAmount(
              weeks.reduce((s, w) => s + w.cashIn, 0)
            )}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex h-3 w-3 flex-none items-start rounded-sm bg-error-500" />
          <span className="text-caption font-caption text-subtext-color">
            Cash Out
          </span>
          <span className="text-body-bold font-body-bold text-default-font">
            {formatPlAmount(
              weeks.reduce((s, w) => s + w.cashOut, 0)
            )}
          </span>
        </div>
      </div>
      <div className="flex w-full items-start gap-4">
        <div className="flex h-64 flex-col items-end justify-between py-1">
          {yAxisLabels.map((label, i) => (
            <span
              key={i}
              className="text-caption font-caption text-neutral-400"
            >
              {label}
            </span>
          ))}
        </div>
        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-3">
          <div className="flex h-64 w-full flex-none gap-2 overflow-visible py-1 relative items-end justify-around">
            {/* Grid lines */}
            <div className="flex items-start border-b border-dashed border-neutral-100 absolute left-0 right-0 top-1" />
            <div className="flex items-start border-b border-dashed border-neutral-100 absolute left-0 right-0 top-[25%]" />
            <div className="flex items-start border-b border-dashed border-neutral-100 absolute left-0 right-0 top-[50%]" />
            <div className="flex items-start border-b border-dashed border-neutral-100 absolute left-0 right-0 top-[75%]" />
            <div className="flex items-start border-b border-dashed border-neutral-100 absolute bottom-1 left-0 right-0" />
            {weeks.map((week, i) => {
              const isHovered = hoveredWeekIndex === i;
              const inH = (week.cashIn / maxVal) * CHART_HEIGHT_PX;
              const outH = (week.cashOut / maxVal) * CHART_HEIGHT_PX;
              const tooltipText = `${week.label}: In ${formatPlAmount(week.cashIn)}, Out ${formatPlAmount(week.cashOut)}`;
              return (
                <div
                  key={i}
                  className="flex flex-col items-center gap-1 relative z-[1] min-w-0 flex-1"
                  onMouseEnter={() => setHoveredWeekIndex(i)}
                  onMouseLeave={() => setHoveredWeekIndex(null)}
                  title={tooltipText}
                  role="img"
                  aria-label={tooltipText}
                >
                  {isHovered && (
                    <div
                      className="absolute bottom-full left-1/2 z-20 mb-1 -translate-x-1/2 rounded-md border border-neutral-200 bg-white px-2 py-1.5 shadow-md min-w-[120px] whitespace-nowrap"
                      role="tooltip"
                    >
                      <span className="text-caption-bold font-caption-bold text-neutral-900 block">
                        {week.label}
                      </span>
                      <span className="text-caption font-caption text-success-600 block">
                        In: {formatPlAmount(week.cashIn)}
                      </span>
                      <span className="text-caption font-caption text-error-600 block">
                        Out: {formatPlAmount(week.cashOut)}
                      </span>
                    </div>
                  )}
                  <div className="flex items-end gap-1 w-full justify-center">
                    <div className="flex flex-col items-center flex-1 min-w-0">
                      <span className="font-['Inter'] text-[10px] font-[500] leading-[15px] text-default-font">
                        {formatPlAmount(week.cashIn)}
                      </span>
                      <div
                        className="flex h-10 w-6 flex-none items-start rounded-t-sm bg-success-500 transition-opacity"
                        style={{
                          height: Math.max(8, inH),
                        }}
                      />
                    </div>
                    <div className="flex flex-col items-center flex-1 min-w-0">
                      <span className="font-['Inter'] text-[10px] font-[500] leading-[15px] text-default-font">
                        {formatPlAmount(week.cashOut)}
                      </span>
                      <div
                        className="flex h-10 w-6 flex-none items-start rounded-t-sm bg-error-500 transition-opacity"
                        style={{
                          height: Math.max(8, outH),
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex w-full items-start justify-around gap-2">
            {weeks.map((w, i) => (
              <div
                key={i}
                className="flex min-w-0 flex-1 flex-col items-center"
              >
                <span className="text-caption font-caption text-neutral-400">
                  {w.label}
                </span>
                <span className="text-[10px] leading-[14px] text-neutral-500 text-center">
                  {formatIsoDateRangeOrdinalMonth(w.startDate, w.endDate) ?? "—"}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
